// current used to update loves,
// import { UPDATE_PROMPT } from './types';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import {
  asyncActionStart,
  asyncActionFinish,
  asyncActionError
} from './asyncActions';
import { firestore } from '../firebase';
import { openSnackbar } from './snackbarActions';
import { prepareEditorStateForStorage } from '../utils/convertEditorState';
import { updateMyUser } from './updateUserActions';
import { dispatchManmadeErrors, resetErrors } from './errorsActions';
import checkPromptObjectBefore, {
  checkPromptObjectBeforeDraft
} from '../utils/checkPromptObjectBefore';
import updateLookingforPreferences from '../utils/updateLookingforPreferences';

// // mininum time you have to wait between bumping each prompt (hours)
// const minTimePerPromptBumpHours = 10;
// minimum time you have to wait between each bump for an user (hours)
const minBumpTimeForUserHours = 20;

// bump the prompt to update it's bumpedAt field with the current date
export const bumpPrompt = promptID => async (dispatch, getState) => {
  // dispatch(asyncActionStart());
  try {
    const { uid: userId } = getState().auth.currentUser;
    const promptRef = doc(firestore, 'prompts', promptID);
    const userPrivateRef = doc(firestore, 'usersPrivate', userId);

    // get the current time in seconds
    const timeRightNow = Math.floor(Date.now() / 1000);

    // get the lastBumpPromptAt from the user's usersPrivate doc
    const userPrivateSnapshot = await getDoc(userPrivateRef);
    const { lastBumpPromptAt } = userPrivateSnapshot.data();

    // if lastBumpPromptAt does not exist yet in userPrivate doc yet, we set lastBumpPromptAtSeconds to 0
    let lastBumpPromptAtSeconds = 0;
    if (lastBumpPromptAt) {
      lastBumpPromptAtSeconds = lastBumpPromptAt.seconds;
    }
    // find the amount of time (in seconds) from now, that needs to be greater than
    // lastBumpPromptAtSeconds, in order to pass the if statement below
    const minBumpTimeForUserSeconds = minBumpTimeForUserHours * 60 * 60;
    const minTimeLapsedForBump = timeRightNow - minBumpTimeForUserSeconds;

    // if bump is currently unavailable,
    // calculate how much time we need to bump again
    const timeToNextBumpSeconds =
      lastBumpPromptAtSeconds + minBumpTimeForUserSeconds - timeRightNow;
    const timeToNextBumpMinutes = timeToNextBumpSeconds / 60;
    const hours = Math.floor(timeToNextBumpMinutes / 60);
    const minutes = Math.floor(timeToNextBumpMinutes % 60);
    const timeToNextBumpString = `${hours} hours and ${minutes} minutes`;
    const timeToNextBumpStringMod = timeToNextBumpString
      .replace(/^0 hours and /, '')
      .replace(/^1 hours/, '1 hour')
      .replace(' 1 minutes', ' 1 minute')
      .replace('0 minutes', 'a few seconds');

    // if minTimeLapsedForBump is greater than bumped time then we'll bump the prompt
    if (minTimeLapsedForBump >= lastBumpPromptAtSeconds) {
      await updateDoc(promptRef, {
        bumpedAt: serverTimestamp()
      });
      await updateDoc(userPrivateRef, {
        lastBumpPromptAt: serverTimestamp()
      });
    } else {
      // if the minTimeLapsedForBump is less than bumped time then we need to wait by throwing the error
      throw new Error(
        `Too soon! Please wait ${timeToNextBumpStringMod} to bump again!`
        // You can bump a prompt once every ${minBumpTimeForUserHours} hours.`
      );
    }
    // dispatch(asyncActionFinish());
    dispatch(
      openSnackbar('SuccessSnackbar', {
        message: 'Successfully bumped the prompt to the top of front page!',
        autoHideDuration: 8000 // auto hide the snackbar longer than usual
      })
    );
  } catch (error) {
    // const { code, message } = error;
    const { message } = error;
    console.error(error);
    // dispatch(asyncActionError({ code, message }));
    dispatch(
      openSnackbar('ErrorSnackbar', { message, autoHideDuration: 8000 }) //  auto hide the snackbar longer than usual
    );
  }
};

export const updatePrompt = (prePromptObject, checkForError = true) => async (
  dispatch,
  getState
) => {
  dispatch(asyncActionStart());
  try {
    // creates a brand new promptObject to NOT mutate anything on original one!
    const promptObject = { ...prePromptObject };
    // the rule-guardrail verdict belongs to promptModerationFunctions.js. It
    // shows up here only because getPrompt spreads the whole document into
    // redux; sending it back would race the moderation write and trip the
    // security rule that pins it. updateDoc merges, so dropping it keeps it.
    delete promptObject.moderation;
    // creates or updates tagsLower with the latest tags,
    // tagsLower is used to fetch prompts based on tags in a case agnostic way
    promptObject.tagsLower = promptObject.tags.map(tag => tag.toLowerCase());

    // checks the promptObject to make sure it does not have empty title or text or other deficiencies
    // throws an error when it does not pass
    // checkForError is false when we are reverting the prompt back to a draft
    // so we do a mild version of error checking, checking only if the title is correct
    if (checkForError) {
      checkPromptObjectBefore(promptObject);
    } else {
      checkPromptObjectBeforeDraft(promptObject);
    }
    // prepare text (draftjs text editor state) ready for database storage
    promptObject.text = prepareEditorStateForStorage(promptObject.text);

    const promptRef = doc(firestore, 'prompts', promptObject.id);

    // get the current promptData to check its published field
    const oldPrompt = await getDoc(promptRef);
    const oldPromptData = oldPrompt.data();
    let newPromptObject = {};
    // check if we are publishing this prompt for the first time,
    // if so, update the publishedAt field to the current time
    if (oldPromptData.publishedAt === null && promptObject.published === true) {
      newPromptObject = {
        ...promptObject,
        publishedAt: serverTimestamp()
      };
    } else {
      newPromptObject = {
        ...promptObject
      };
    }

    await updateDoc(promptRef, {
      ...newPromptObject,
      editedAt: serverTimestamp()
    });

    // if the prompt's lookingfor and iam are different from myUser's lookingforPreferences
    // then update the lookingforPreferences in the user's document in usersPRivate collection
    updateLookingforPreferences({
      getState,
      promptObject,
      dispatch,
      updateMyUser
    });

    // if prompt is successfully updated, remove all errors from redux state
    dispatch(resetErrors());

    dispatch(asyncActionFinish());
    return 'success';
  } catch (error) {
    // if the error is manmade, made in checkPromptObjectBefore.js
    if (error.type === 'manmade') {
      // this branch used to end the action without ever clearing the loading
      // flag asyncActionStart() set, which leaves Post Prompt / Save Draft /
      // Cancel disabled={loading} forever - the user has to reload to try again
      dispatch(asyncActionFinish());
      dispatch(dispatchManmadeErrors(error));
      // a manmade error otherwise only paints inline text and never scrolls
      // into view, so a rule refusal would read as a dead button
      if (error.rules) {
        dispatch(
          openSnackbar('ErrorSnackbar', {
            message:
              'This prompt breaks the site rules - see the details at the top of the form.',
            autoHideDuration: 8000
          })
        );
      }
    } else {
      // if the error is generated by firebase
      const { code, message } = error;
      console.error(error);
      dispatch(asyncActionError({ code, message }));
      dispatch(openSnackbar('ErrorSnackbar', { message }));
    }
  }
  return null;
  // dispatch({ type: UPDATE_PROMPT, payload: promptObject });
};
